
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { College } from "@/hooks/useCollegeSearch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bookmark, ExternalLink, AlertCircle, MapPin, DollarSign, Users, Building, Pencil, School, Book, Trophy, Coins } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";

interface CollegeModalProps {
  college: College | null;
  open: boolean;
  onClose: () => void;
  onToggleSave: (collegeId: string) => void;
  onSuggestCorrection: (collegeId: string) => void;
}

const CollegeModal: React.FC<CollegeModalProps> = ({ 
  college, 
  open, 
  onClose,
  onToggleSave,
  onSuggestCorrection
}) => {
  if (!college) return null;

  const formatCurrency = (value: number | null) => {
    if (value === null) return 'N/A';
    return `$${value.toLocaleString()}`;
  };

  // Helper to check if a sport is offered
  const getSportStatus = (sportKey: string) => {
    if (!college) return false;
    return college[sportKey] === true;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            {college.school_logo ? (
              <div className="w-20 h-20 flex-shrink-0">
                <AspectRatio ratio={1/1} className="bg-background overflow-hidden rounded-lg border">
                  <img 
                    src={college.school_logo} 
                    alt={`${college.name} logo`} 
                    className="object-contain h-full w-full" 
                  />
                </AspectRatio>
              </div>
            ) : (
              <div className="w-20 h-20 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
                <School className="h-10 w-10 text-gray-400" />
              </div>
            )}
            <div>
              <DialogTitle className="text-2xl">{college.name}</DialogTitle>
              <DialogDescription className="flex items-center mt-1 text-base">
                <MapPin className="h-4 w-4 mr-1" />
                {college.city}, {college.state}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="general" className="mt-4">
          <TabsList className="w-full grid grid-cols-4 gap-2 bg-muted p-1 rounded-lg mb-4">
            <TabsTrigger 
              value="general" 
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm font-medium"
            >
              <School className="h-4 w-4" />
              <span>General</span>
            </TabsTrigger>
            <TabsTrigger 
              value="academic" 
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm font-medium"
            >
              <Book className="h-4 w-4" />
              <span>Academic</span>
            </TabsTrigger>
            <TabsTrigger 
              value="athletic" 
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm font-medium"
            >
              <Trophy className="h-4 w-4" />
              <span>Athletic</span>
            </TabsTrigger>
            <TabsTrigger 
              value="financial" 
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-md data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm font-medium"
            >
              <Coins className="h-4 w-4" />
              <span>Financial</span>
            </TabsTrigger>
          </TabsList>
          
          {/* General Tab */}
          <TabsContent value="general" className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building className="h-4 w-4 text-primary" />
                  </div>
                  <h4 className="text-sm font-semibold text-slate-700">Institution Type</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {college.public_private && (
                    <Badge className="bg-primary/90 text-primary-foreground hover:bg-primary">
                      {college.public_private}
                    </Badge>
                  )}
                  {college.religious_affiliation && (
                    <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-200">
                      {college.religious_affiliation}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <MapPin className="h-4 w-4 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-blue-800">Location</h4>
                </div>
                <p className="text-sm font-medium text-blue-900">{college.city}, {college.state}</p>
                {college.campus && (
                  <Badge variant="outline" className="mt-2 border-blue-300 text-blue-700">
                    {college.campus}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-xl border border-emerald-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Users className="h-4 w-4 text-emerald-600" />
                </div>
                <h4 className="text-sm font-semibold text-emerald-800">Enrollment</h4>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-emerald-600 text-white text-base px-3 py-1">
                  {college.undergraduate_enrollment 
                    ? college.undergraduate_enrollment.toLocaleString() + " students"
                    : 'N/A'}
                </Badge>
                {college.male_female_ratio && (
                  <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                    M/F Ratio: {college.male_female_ratio}
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Building className="h-4 w-4 text-amber-600" />
                </div>
                <h4 className="text-sm font-semibold text-amber-800">Campus Housing</h4>
              </div>
              <Badge className={college.has_campus_housing 
                ? "bg-green-500 text-white" 
                : "bg-gray-200 text-gray-700"}>
                {college.has_campus_housing ? '✓ Available' : '✗ Not Available'}
              </Badge>
            </div>
          </TabsContent>
          
          {/* Academic Tab */}
          <TabsContent value="academic" className="space-y-5">
            {/* Top Stats Row */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 p-4 rounded-xl border border-indigo-200 shadow-sm text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-2 bg-indigo-500/20 rounded-lg">
                    <Book className="h-4 w-4 text-indigo-600" />
                  </div>
                </div>
                <div className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Average GPA</div>
                <div className="text-2xl font-bold text-indigo-800 mt-1">{college.average_gpa || 'N/A'}</div>
              </div>
              <div className="bg-gradient-to-br from-violet-50 to-violet-100 p-4 rounded-xl border border-violet-200 shadow-sm text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <School className="h-4 w-4 text-violet-600" />
                  </div>
                </div>
                <div className="text-xs font-medium text-violet-600 uppercase tracking-wide">Average SAT</div>
                <div className="text-2xl font-bold text-violet-800 mt-1">{college.average_sat_score || 'N/A'}</div>
                {(college.sat_25th_percentile || college.sat_75th_percentile) && (
                  <div className="text-xs text-violet-600 mt-1">
                    {college.sat_25th_percentile || '?'} - {college.sat_75th_percentile || '?'}
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-br from-fuchsia-50 to-fuchsia-100 p-4 rounded-xl border border-fuchsia-200 shadow-sm text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-2 bg-fuchsia-500/20 rounded-lg">
                    <School className="h-4 w-4 text-fuchsia-600" />
                  </div>
                </div>
                <div className="text-xs font-medium text-fuchsia-600 uppercase tracking-wide">Average ACT</div>
                <div className="text-2xl font-bold text-fuchsia-800 mt-1">{college.average_act_score || 'N/A'}</div>
                {(college.act_25th_percentile || college.act_75th_percentile) && (
                  <div className="text-xs text-fuchsia-600 mt-1">
                    {college.act_25th_percentile || '?'} - {college.act_75th_percentile || '?'}
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-teal-100 p-4 rounded-xl border border-emerald-200 shadow-sm text-center">
                <div className="flex justify-center mb-2">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Users className="h-4 w-4 text-emerald-600" />
                  </div>
                </div>
                <div className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Enrollment</div>
                <div className="text-2xl font-bold text-emerald-800 mt-1">
                  {college.undergraduate_enrollment ? college.undergraduate_enrollment.toLocaleString() : 'N/A'}
                </div>
              </div>
            </div>
            
            {/* SAT/ACT Percentile Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 p-4 rounded-xl border border-violet-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-violet-500/20 rounded-lg">
                    <School className="h-4 w-4 text-violet-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-violet-800">SAT Score Range</h4>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <Badge variant="outline" className="border-violet-300 text-violet-700 mb-1">25th %</Badge>
                    <div className="text-lg font-bold text-violet-800">{college.sat_25th_percentile || 'N/A'}</div>
                  </div>
                  <div className="text-violet-400">—</div>
                  <div className="text-center">
                    <Badge variant="outline" className="border-violet-300 text-violet-700 mb-1">75th %</Badge>
                    <div className="text-lg font-bold text-violet-800">{college.sat_75th_percentile || 'N/A'}</div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-fuchsia-50 to-pink-50 p-4 rounded-xl border border-fuchsia-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-fuchsia-500/20 rounded-lg">
                    <School className="h-4 w-4 text-fuchsia-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-fuchsia-800">ACT Score Range</h4>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <Badge variant="outline" className="border-fuchsia-300 text-fuchsia-700 mb-1">25th %</Badge>
                    <div className="text-lg font-bold text-fuchsia-800">{college.act_25th_percentile || 'N/A'}</div>
                  </div>
                  <div className="text-fuchsia-400">—</div>
                  <div className="text-center">
                    <Badge variant="outline" className="border-fuchsia-300 text-fuchsia-700 mb-1">75th %</Badge>
                    <div className="text-lg font-bold text-fuchsia-800">{college.act_75th_percentile || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Programs of Study */}
            <div className="bg-gradient-to-br from-cyan-50 to-sky-50 p-4 rounded-xl border border-cyan-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-cyan-500/20 rounded-lg">
                  <Book className="h-4 w-4 text-cyan-600" />
                </div>
                <h4 className="text-sm font-semibold text-cyan-800">Programs of Study</h4>
              </div>
              <p className="text-sm text-cyan-900">{college.programs_of_study || 'Program information not available'}</p>
            </div>
            
            {/* Application Information */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-4 rounded-xl border border-rose-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-rose-500/20 rounded-lg">
                  <ExternalLink className="h-4 w-4 text-rose-600" />
                </div>
                <h4 className="text-sm font-semibold text-rose-800">Application Links</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {college.application_website && (
                  <a 
                    href={college.application_website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-700 rounded-lg text-sm font-medium hover:bg-rose-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Apply Now
                  </a>
                )}
                {college.admissions_website && (
                  <a 
                    href={college.admissions_website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-700 rounded-lg text-sm font-medium hover:bg-rose-200 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3 w-3" />
                    Admissions Info
                  </a>
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Athletic Tab */}
          <TabsContent value="athletic" className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-4 rounded-xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Trophy className="h-4 w-4 text-orange-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-orange-800">Athletics Division</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {college.division && (
                    <Badge className="bg-orange-500 text-white text-sm px-3 py-1">
                      {college.division}
                    </Badge>
                  )}
                  {college.conference && (
                    <Badge variant="outline" className="border-orange-300 text-orange-700">
                      {college.conference}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-4 rounded-xl border border-pink-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-pink-500/20 rounded-lg">
                    <School className="h-4 w-4 text-pink-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-pink-800">Mascot & Colors</h4>
                </div>
                <p className="text-sm font-medium text-pink-900">{college.mascot || 'N/A'}</p>
                <p className="text-xs text-pink-700 mt-1">{college.colors || 'Colors not listed'}</p>
              </div>
            </div>
            
            {/* Men's Sports */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Trophy className="h-4 w-4 text-blue-600" />
                </div>
                <h4 className="text-sm font-semibold text-blue-800">Men's Sports</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {['basketball_men', 'baseball_men', 'football_men', 'soccer_men', 
                  'track_field_xcountry_men', 'track_field_indoor_men', 'track_field_outdoor_men',
                  'swimming_men', 'golf_men', 'tennis_men', 'volleyball_men', 'wrestling_men',
                  'lacrosse_men'].map(sportKey => 
                  getSportStatus(sportKey) ? (
                    <Badge key={sportKey} className="bg-blue-100 text-blue-800 hover:bg-blue-200 border border-blue-200">
                      {sportKey.replace('_men', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ) : null
                )}
              </div>
            </div>
            
            {/* Women's Sports */}
            <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-xl border border-purple-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Trophy className="h-4 w-4 text-purple-600" />
                </div>
                <h4 className="text-sm font-semibold text-purple-800">Women's Sports</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {['basketball_women', 'softball_women', 'soccer_women', 
                  'track_field_xcountry_women', 'track_field_indoor_women', 'track_field_outdoor_women',
                  'swimming_women', 'golf_women', 'tennis_women', 'volleyball_women', 'field_hockey_women',
                  'lacrosse_women', 'gymnastics_women'].map(sportKey => 
                  getSportStatus(sportKey) ? (
                    <Badge key={sportKey} className="bg-purple-100 text-purple-800 hover:bg-purple-200 border border-purple-200">
                      {sportKey.replace('_women', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  ) : null
                )}
              </div>
            </div>
          </TabsContent>
          
          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-green-100 p-4 rounded-xl border border-emerald-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-emerald-800">In-State Tuition</h4>
                </div>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(college.in_state_tuition)}</p>
                <Badge className="mt-2 bg-emerald-100 text-emerald-700 border border-emerald-200">Per Year</Badge>
              </div>
              
              <div className="bg-gradient-to-br from-teal-50 to-cyan-100 p-4 rounded-xl border border-teal-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 bg-teal-500/20 rounded-lg">
                    <DollarSign className="h-4 w-4 text-teal-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-teal-800">Out-of-State Tuition</h4>
                </div>
                <p className="text-2xl font-bold text-teal-700">{formatCurrency(college.out_state_tuition)}</p>
                <Badge className="mt-2 bg-teal-100 text-teal-700 border border-teal-200">Per Year</Badge>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-slate-50 to-gray-100 p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-slate-500/20 rounded-lg">
                  <Coins className="h-4 w-4 text-slate-600" />
                </div>
                <h4 className="text-sm font-semibold text-slate-700">Financial Aid Information</h4>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-slate-300">FAFSA</Badge>
                  <span>Required: Information not available</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-slate-300">Aid</Badge>
                  <span>Average Award: Information not available</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-slate-300">Merit</Badge>
                  <span>Scholarships: Information not available</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 rounded-xl border border-primary/20 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/20 rounded-lg flex-shrink-0">
                  <AlertCircle className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-primary">💡 Financial Aid Tip</p>
                  <p className="text-sm text-muted-foreground mt-1">Complete your FAFSA as early as possible to maximize your aid opportunities. The FAFSA opens on October 1st each year.</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Buttons - Always visible at bottom */}
        <div className="flex flex-wrap gap-3 mt-6 pt-3 border-t sticky bottom-0 bg-background">
          <Button
            variant={college.isSaved ? "secondary" : "outline"}
            onClick={() => onToggleSave(college.id)}
            className="flex-1"
          >
            <Bookmark 
              className="h-4 w-4 mr-2" 
              fill={college.isSaved ? "currentColor" : "none"} 
            />
            {college.isSaved ? "Saved" : "Save College"}
          </Button>
          
          {college.main_website && (
            <Button
              variant="outline"
              asChild
              className="flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <a href={college.main_website} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Visit Website
              </a>
            </Button>
          )}
          
          <Button
            variant="outline"
            onClick={() => onSuggestCorrection(college.id)}
            className="flex-1"
          >
            <Pencil className="h-4 w-4 mr-2" />
            Suggest a Fix
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CollegeModal;
